import { isPortfolioHost } from "@/lib/portfolio-host";
import { useEffect, useRef } from "react";
import { toast } from "sonner";

// Poll interval: 5 minutes
const POLL_INTERVAL = 5 * 60 * 1000;

// Embedded at build time by Vite's define config
const currentBuildId = typeof __BUILD_ID__ !== "undefined" ? __BUILD_ID__ : "";

/**
 * Polls /version.json to detect new deployments and shows a toast
 * prompting the user to refresh. Non-intrusive — the user chooses
 * when to reload so they don't lose in-progress work.
 */
export function useVersionCheck() {
  const toastShownRef = useRef(false);

  useEffect(() => {
    // Skip in dev mode or if build ID isn't available. Also on the
    // marketing-domain portfolio host (EPD-10104), which serves nothing but
    // /portfolio/<slug>: /version.json there is the marketing site's.
    if (import.meta.env.DEV || !currentBuildId || isPortfolioHost()) return;

    // App loaded successfully with current chunks — clear the stale-chunk
    // reload flag so the next deployment can still auto-reload once.
    try {
      sessionStorage.removeItem("stale-chunk-reload");
    } catch {
      // sessionStorage unavailable — ignore
    }

    const check = async () => {
      try {
        const res = await fetch("/version.json", { cache: "no-cache" });
        if (!res.ok) return;

        const { id } = (await res.json()) as { id: string };
        if (id && id !== currentBuildId && !toastShownRef.current) {
          toastShownRef.current = true;
          toast("A new version is available", {
            description: "Refresh to get the latest updates.",
            duration: Infinity,
            action: {
              label: "Refresh",
              onClick: () => window.location.reload(),
            },
          });
        }
      } catch {
        // Network error — ignore silently
      }
    };

    // Check once on mount, then poll
    check();
    const interval = setInterval(check, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, []);
}
